/**
 * Bug Report Kanban View
 *
 * Uses the global Kanban component with bug-reports-specific configuration.
 * All column definitions come from bugReportStatusConfig.ts
 */

import { useState, useEffect, useMemo } from 'react';
import { BugReport, BugStatus } from 'shared/types/bug-report';
import { BugReportCard } from './BugReportCard';
import { Kanban } from 'shared/components/Kanban';
import { bugReportsKanbanConfig } from 'shared/components/Kanban/configs/bugReportsKanbanConfig';
import { bugPhaseColumns } from '../bugReportPhaseConfig';
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ITEMS_PER_PAGE = 12;

interface BugReportKanbanViewProps {
  bugs: BugReport[];
  onUpdate: () => void;
  onBugClick?: (bug: BugReport) => void;
}

interface PendingPark {
  bugId: string | number;
  oldStatus: BugStatus;
}

export const BugReportKanbanView = ({
  bugs,
  onUpdate,
  onBugClick
}: BugReportKanbanViewProps) => {
  const { session } = useSession();

  const [localBugs, setLocalBugs] = useState(bugs);
  const [columnLimits, setColumnLimits] = useState<Record<string, number>>({});

  // Park reason dialog state
  const [pendingPark, setPendingPark] = useState<PendingPark | null>(null);
  const [parkReason, setParkReason] = useState('');
  const [isParking, setIsParking] = useState(false);

  useEffect(() => {
    setLocalBugs(bugs);
    // Reset per-column limits when the bug list changes (e.g. after filter)
    setColumnLimits({});
  }, [bugs]);

  const getColumnLimit = (status: BugStatus) => columnLimits[status] ?? ITEMS_PER_PAGE;

  // Only pass the visible slice of items per column to the Kanban board
  const paginatedBugs = useMemo(() => {
    const byStatus = new Map<BugStatus, BugReport[]>();
    localBugs.forEach(bug => {
      const list = byStatus.get(bug.status) ?? [];
      list.push(bug);
      byStatus.set(bug.status, list);
    });
    const result: BugReport[] = [];
    byStatus.forEach((items, status) => {
      result.push(...items.slice(0, getColumnLimit(status)));
    });
    return result;
  }, [localBugs, columnLimits]);

  // Column-level load-more config
  const columnPagination = useMemo(() =>
    bugPhaseColumns.map(col => {
      const total = localBugs.filter(b => b.status === col.id).length;
      const limit = getColumnLimit(col.id);
      return {
        columnId: col.id as BugStatus,
        hasMore: total > limit,
        isLoadingMore: false,
        onLoadMore: () =>
          setColumnLimits(prev => ({
            ...prev,
            [col.id]: (prev[col.id] ?? ITEMS_PER_PAGE) + ITEMS_PER_PAGE,
          })),
      };
    }),
    [localBugs, columnLimits]
  );

  const executeStatusUpdate = async (
    bugId: string | number,
    newStatus: BugStatus,
    oldStatus: BugStatus,
    reason?: string
  ) => {
    setLocalBugs(prev =>
      prev.map(b =>
        b.id === bugId
          ? { ...b, status: newStatus, ...(reason ? { parkReason: reason } : {}) }
          : b
      )
    );

    try {
      const body: { status: BugStatus; parkReason?: string } = { status: newStatus };
      if (reason) body.parkReason = reason;

      const response = await fetchWithAuth(
        `${API_URL}/bug-reports/${bugId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        session
      );

      if (!response.ok) throw new Error("Failed to update bug status");

      toast.success(`Bug verplaatst naar "${newStatus}"`);
    } catch (error: any) {
      console.error("Error updating bug status:", error);
      toast.error(`Fout bij verplaatsen bug: ${error.message || 'Onbekende fout'}`);

      setLocalBugs(prev =>
        prev.map(b => b.id === bugId ? { ...b, status: oldStatus } : b)
      );
      onUpdate();
    }
  };

  const handleDrop = async (bugId: string | number, newStatus: BugStatus, oldStatus: BugStatus) => {
    if (newStatus === BugStatus.PARKED) {
      // Optimistically move card, then ask for reason
      setLocalBugs(prev =>
        prev.map(b => b.id === bugId ? { ...b, status: newStatus } : b)
      );
      setPendingPark({ bugId, oldStatus });
      setParkReason('');
      return;
    }
    await executeStatusUpdate(bugId, newStatus, oldStatus);
  };

  const handleParkConfirm = async () => {
    if (!pendingPark) return;
    setIsParking(true);
    try {
      await executeStatusUpdate(pendingPark.bugId, BugStatus.PARKED, pendingPark.oldStatus, parkReason);
    } finally {
      setIsParking(false);
      setPendingPark(null);
      setParkReason('');
    }
  };

  const handleParkCancel = () => {
    if (!pendingPark) return;
    setLocalBugs(prev =>
      prev.map(b => b.id === pendingPark.bugId ? { ...b, status: pendingPark.oldStatus } : b)
    );
    setPendingPark(null);
    setParkReason('');
  };

  // Total counts per column based on ALL bugs (not just the paginated slice)
  const headerRow = useMemo(() =>
    bugPhaseColumns.map(col => {
      const total = localBugs.filter(b => b.status === col.id).length;
      return (
        <div key={String(col.id)} className="bg-white rounded-lg border border-neutral-200 px-4 py-3">
          <div className={`text-2xl font-semibold ${col.color}`}>{total}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{col.label}</div>
        </div>
      );
    }),
    [localBugs]
  );

  const renderBugCard = (bug: BugReport) => (
    <BugReportCard
      bug={bug}
      onClick={() => onBugClick?.(bug)}
    />
  );

  return (
    <>
      <Kanban
        config={bugReportsKanbanConfig}
        items={paginatedBugs}
        renderCard={renderBugCard}
        onDrop={handleDrop}
        onCardClick={onBugClick}
        columnPagination={columnPagination}
        headerRow={headerRow}
      />

      <Dialog open={!!pendingPark} onOpenChange={(open) => { if (!open) handleParkCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reden voor parkeren</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="park-reason">
              Waarom wordt dit item geparkeerd?
            </Label>
            <Textarea
              id="park-reason"
              placeholder="Geef een reden op..."
              value={parkReason}
              onChange={(e) => setParkReason(e.target.value)}
              rows={3}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleParkCancel} disabled={isParking}>
              Annuleren
            </Button>
            <Button onClick={handleParkConfirm} disabled={!parkReason.trim() || isParking}>
              {isParking ? 'Bezig...' : 'Bevestigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
